const NUMBER_FIELD = 'input[type="number"], input[inputmode="decimal"], input[inputmode="numeric"]';

const isEditableNumberField = (target: EventTarget | null): target is HTMLInputElement =>
  target instanceof HTMLInputElement && target.matches(NUMBER_FIELD) && !target.readOnly && !target.disabled;

/**
 * Highlights a number field's whole value when it gains focus, so typing replaces it.
 *
 * Number fields start with a real 0, and the browser puts the cursor where the user clicked:
 * in a right-aligned price that is in front of the 0, so typing 1 gave "10". One page-wide
 * listener covers every field, modals and offcanvases included, without each standalone
 * component importing a directive.
 *
 * Returns a function that removes the listeners.
 */
export function selectNumberFieldsOnFocus(doc: Document): () => void {
  let clickedInto: HTMLInputElement | null = null;

  // Deferred because the click that focused the field places the cursor after focus fires,
  // and some fields rewrite their value on focus.
  const selectSoon = (input: HTMLInputElement) =>
    setTimeout(() => {
      if (doc.activeElement === input) input.select();
    });

  const onMouseDown = (event: MouseEvent) => {
    const target = event.target;
    clickedInto = isEditableNumberField(target) && doc.activeElement !== target ? target : null;
  };

  const onFocusIn = (event: FocusEvent) => {
    if (isEditableNumberField(event.target)) selectSoon(event.target);
  };

  // Selected at once because on a busy page, keys typed straight after the click run before
  // any timer; and again after, because Safari collapses the highlight on release.
  // Not preventDefault: that would also skip the spin buttons' mouseup, which stops their repeat.
  const onMouseUp = (event: MouseEvent) => {
    const input = clickedInto;
    clickedInto = null;
    if (input && event.target === input) {
      input.select();
      selectSoon(input);
    }
  };

  doc.addEventListener('mousedown', onMouseDown, true);
  doc.addEventListener('focusin', onFocusIn);
  doc.addEventListener('mouseup', onMouseUp, true);

  return () => {
    doc.removeEventListener('mousedown', onMouseDown, true);
    doc.removeEventListener('focusin', onFocusIn);
    doc.removeEventListener('mouseup', onMouseUp, true);
  };
}
